import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  TrackingRegistryService,
  type TrackingRegistryResponseDto,
} from 'src/generated-sources/openapi/wallet-service/tracking-registry';
import {
  WalletService,
  type WalletResponseDto,
} from 'src/generated-sources/openapi/wallet-service/wallet';
import { SnackbarService } from 'src/app/services/snackbar.service';
import type { TrackingRegistryTableView } from './wallet-details-table.config';
import { PersonType } from 'src/app/services/models/enums';
import { NO_NAME } from 'src/app/components/constants';
import {
  WalletDetailsErrorHandlerService,
  WalletDetailsErrorType,
} from './wallet-details-error-handler.service';

export interface PageableResponse<T> {
  items: T[];
  total: number;
}

@Injectable({ providedIn: 'any' })
export class WalletDetailsFacade {
  private readonly trackingRegistryService = inject(TrackingRegistryService);
  private readonly walletService = inject(WalletService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly errorHandler = inject(WalletDetailsErrorHandlerService);

  loadTrackingRegistries(
    walletId: string,
    page: number,
    pageSize: number,
    sortField?: string,
    sortDirection?: 'asc' | 'desc',
  ): Observable<PageableResponse<TrackingRegistryTableView>> {
    return this.trackingRegistryService
      .listPageable({
        walletId,
        page,
        perPage: pageSize,
        sort: sortField,
        dir: sortDirection,
      })
      .pipe(
        map((response) => ({
          items: (response.items || []).map(this.transformToView),
          total: response.total || 0,
        })),
        catchError((error) => {
          this.errorHandler.handle(error, WalletDetailsErrorType.LOAD_REGISTRIES);
          return of({ items: [], total: 0 });
        }),
      );
  }

  deleteWallet(walletId: string, walletName: string): Observable<void> {
    return this.walletService.deleteById({ id: walletId }).pipe(
      map(() => {
        this.snackbarService.normal(
          $localize`:@@wasDeletedCorrectly:Se eliminó correctamente: ${walletName}`,
        );
      }),
      catchError((error) => {
        this.errorHandler.handle(error, WalletDetailsErrorType.DELETE_WALLET);
        throw error;
      }),
    );
  }

  toggleFavorite(walletId: string): Observable<void> {
    return this.walletService.toggleFavorite({ id: walletId }).pipe(
      catchError((error) => {
        this.errorHandler.handle(error, WalletDetailsErrorType.TOGGLE_FAVORITE);
        throw error;
      }),
    );
  }

  findWalletById(walletId: string): Observable<WalletResponseDto> {
    return this.walletService.findById({ id: walletId }).pipe(
      catchError((error) => {
        this.errorHandler.handle(error, WalletDetailsErrorType.FIND_WALLET);
        throw error;
      }),
    );
  }

  private transformToView(dto: TrackingRegistryResponseDto): TrackingRegistryTableView {
    return {
      document: dto.document || '-',
      type: dto.type === 'PERSON' ? PersonType.PERSON : PersonType.COMPANY,
      name: dto.name || NO_NAME,
    };
  }
}
